<template>
  <div class="whole-step-container prosthetic-v2-container surgery-container">
    <!-- 위쪽: 새로운 치아 입력 카드 (항상 표시) -->
    <div class="input-card-section" data-tour="s3-input">
      <zirconia-tooth-info-form
        ref="inputCard"
        :key="`input-card-${inputCardKey}`"
        :array-index="-1"
        :tooth-info="inputToothInfo"
        :selected-teeth-numbers="selectedTeethNumbers"
        :prev-teeth-nos="prevTeethNos"
        :window-inner-width-greater-than-1000="true"
        :certified-companies="companiesByApi.certifiedCompanies"
        :not-certified-companies="companiesByApi.notCertifiedCompanies"
        :modal-show-history="modals.history"
        :other-assigned-random-nos="getOtherAssignedRandomNos(-1)"
        :self-issued-map="selfIssuedMap"
        @update:tooth-info="uiHandler.updateInputToothInfo"
        @update:modal-show-history="updateModalShowHistory"
        @update-manufacturer-list="updateManufacturerList"
        @self-issue-detected="uiHandler.relaySelfIssueDetected"
      />

      <!-- 추가하기 버튼 -->
      <div class="add-button-container" data-tour="s3-add">
        <a-button
          class="add-card-button"
          type="primary"
          @click="uiHandler.confirmAndAddCard"
        >
          {{ $t("workorder_btn_add") }}
        </a-button>
      </div>
    </div>

    <!-- 아래쪽: 확정된 카드들 (탭으로 표시) -->
    <div v-if="confirmedTeeth.length > 0" class="confirmed-cards-section">
      <!-- 탭 UI -->
      <div class="card-tabs-container" data-tour="s3-cards">
        <button
          v-for="(tooth, index) in confirmedTeeth"
          :key="`tab-${index}`"
          class="card-tab"
          :class="{ active: currentConfirmedIndex === index }"
          @click="uiHandler.switchConfirmedCard(index)"
        >
          <span class="tab-label">{{ getCardTabLabel(tooth, index) }}</span>
          <button
            class="tab-close-btn"
            @click.stop="uiHandler.removeConfirmedCard(index)"
          >
            <i class="fa-solid fa-xmark" />
          </button>
        </button>
      </div>

      <!-- 현재 선택된 확정 카드 표시 (수정 가능) -->
      <div class="confirmed-card-display">
        <zirconia-tooth-info-form
          :ref="`confirmedCard-${currentConfirmedIndex}`"
          :key="`confirmed-${currentConfirmedIndex}`"
          :array-index="currentConfirmedIndex"
          :tooth-info="confirmedTeeth[currentConfirmedIndex]"
          :selected-teeth-numbers="
            getOtherConfirmedTeethNumbers(currentConfirmedIndex)
          "
          :prev-teeth-nos="prevTeethNos"
          :window-inner-width-greater-than-1000="true"
          :certified-companies="companiesByApi.certifiedCompanies"
          :not-certified-companies="companiesByApi.notCertifiedCompanies"
          :modal-show-history="modals.history"
          :other-assigned-random-nos="
            getOtherAssignedRandomNos(currentConfirmedIndex)
          "
          :self-issued-map="selfIssuedMap"
          @update:tooth-info="uiHandler.updateConfirmedToothInfo"
          @update:modal-show-history="updateModalShowHistory"
          @update-manufacturer-list="updateManufacturerList"
          @self-issue-detected="uiHandler.relaySelfIssueDetected"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ZirconiaToothInfoForm from "./ZirconiaToothInfoForm.vue";
import { getManufacturers } from "@/apis/chinaManufacturer";
import { isDentalmaxGroup } from "../utils/manufacturerGroup";
import { twoBtnDialog, oneBtnDialog } from "@/utils/utils";

function createProstheticTooth() {
  return {
    toothNumber: null,
    toothNumbers: [],
    treatmentType: null,
    group: "",
    itemName: "",
    isCertified: true,
    willTypeProductInfoStepOne: false,
    manufacturerId: "",
    detailSelection: "ZIRCONIA",
    productInfoWithoutCode: {
      brand: "",
      customBrand: "",
      spec: "",
      customSpec: "",
    },
    productInfoWithCode: {
      id: "",
      productCode: "",
      itemName: "",
      description: "",
      gtinCode: "",
      manufacturingDate: "",
      lotNumber: "",
    },
    switches: {
      stm: false,
    },
    inputMode: "barcode",
    // SPEC-CHINA-CERT-CARD-LOOKUP-UNIFY-001 REQ-FE-STEP3-003 — 선택된 실물카드의 randomNo
    randomNo: "",
    // SPEC-DENTALMAX-MULTI-TOOTH-ISSUE-001 Wave2 DEC-009 — 카드(실물카드/제품) 배열.
    // 순서=선택 순서. 덴탈맥스 태그형 멀티선택 전용(비-덴탈맥스는 항상 빈 배열, 스칼라 필드 유지).
    products: [],
  };
}

export default {
  name: "Step3",
  components: {
    ZirconiaToothInfoForm,
  },
  props: {
    initialData: {
      type: Object,
      default: () => ({}),
    },
    // index.vue의 detectedSelfIssues({randomNo: baseline{teeth,products,patient}}) — 저장 시 diff
    // 기준값으로도 쓰이는 durable 상태라 카드 :key 리마운트·스텝 이동에도 소실되지 않는다. 이 prop을
    // 그대로 각 ZirconiaToothInfoForm(입력카드/확정카드)에 내려 배너·가드가 여기서 파생되게 한다.
    selfIssuedMap: {
      type: Object,
      default: () => ({}),
    },
  },
  // 환자 자가발급(자가발급 인증서) 역연결 감지를 상위(index.vue)로 릴레이
  emits: ["self-issue-detected"],
  data() {
    return {
      inputToothInfo: createProstheticTooth(),
      confirmedTeeth: [],
      currentConfirmedIndex: 0,
      inputCardKey: 0,
      includeInputInPublish: false,
      prevTeethNos: [],
      modals: {
        history: {
          stm: false,
        },
      },
      companiesByApi: {
        certifiedCompanies: [],
        // API 응답이 도착하면 통째로 교체되는 초기 placeholder. data() 시점 번역이라
        // 세션 중 locale을 바꾸면 이 항목만 이전 언어로 남지만, 노출 구간이 로딩 순간뿐이라 수용.
        notCertifiedCompanies: [
          { name: this.$t('workorder_company_etc'), engName: "etc", isCertified: false },
        ],
      },
      uiHandler: null,
    };
  },
  computed: {
    selectedTeethNumbers() {
      const allNumbers = [];
      this.confirmedTeeth.forEach((item) => {
        if (item.toothNumbers && item.toothNumbers.length > 0) {
          allNumbers.push(...item.toothNumbers);
        }
      });
      return allNumbers.filter((num) => num !== null);
    },
  },
  created() {
    this.uiHandler = this.createUiHandler();
  },
  mounted() {
    console.log("[Step3 mounted] initialData:", this.initialData);
    this.loadManufacturerList();
    this.restoreInitialData();
  },
  methods: {
    createUiHandler() {
      return {
        updateInputToothInfo: (updatedInfo) => {
          this.inputToothInfo = { ...updatedInfo };
        },
        updateConfirmedToothInfo: (updatedInfo) => {
          if (
            this.currentConfirmedIndex >= 0 &&
            this.currentConfirmedIndex < this.confirmedTeeth.length
          ) {
            this.confirmedTeeth.splice(this.currentConfirmedIndex, 1, {
              ...updatedInfo,
            });
          }
        },
        confirmAndAddCard: async () => {
          const hasToothNumber =
            this.inputToothInfo.toothNumbers &&
            this.inputToothInfo.toothNumbers.length > 0;

          if (!hasToothNumber) {
            this.$message.warning(this.$t("workorder_err_tooth_msg"));
            return;
          }
          if (!this.inputToothInfo.treatmentType) {
            this.$message.warning(this.$t("workorder_err_surgery_msg"));
            return;
          }
          if (!this.inputToothInfo.group) {
            this.$message.warning(this.$t("workorder_err_manufacturer_msg"));
            return;
          }

          // 덴탈맥스: 실물카드 검색·선택 필수 검증 — 최소 1장 이상, randomNo 보유 카드만 인정.
          // (SPEC-CHINA-CERT-CARD-LOOKUP-UNIFY-001 REQ-FE-STEP3-001/003, Wave2 REQ-ADM-MULTI-007 제품 0개 카드 확정 불가)
          // hasCardProduct(SSOT)로 판정 — products.length만 보는 임시 체크로 되돌리지 말 것.
          if (
            isDentalmaxGroup(this.inputToothInfo.group) &&
            !this.hasCardProduct(this.inputToothInfo)
          ) {
            this.$message.warning(this.$t("workorder_err_card_required"));
            return;
          }

          const confirmedData = {
            ...this.inputToothInfo,
            toothNumbers: [...this.inputToothInfo.toothNumbers],
          };

          this.inputToothInfo = createProstheticTooth();
          await this.$nextTick();

          this.confirmedTeeth.push(confirmedData);
          this.currentConfirmedIndex = this.confirmedTeeth.length - 1;
          this.inputCardKey++;
        },
        switchConfirmedCard: (index) => {
          this.currentConfirmedIndex = index;
        },
        removeConfirmedCard: (index) => {
          this.confirmedTeeth.splice(index, 1);
          if (this.currentConfirmedIndex >= this.confirmedTeeth.length) {
            this.currentConfirmedIndex = Math.max(
              0,
              this.confirmedTeeth.length - 1
            );
          }
        },
        // ZirconiaToothInfoForm(입력카드/확정카드 공통)이 감지한 환자 자가발급 역연결을
        // index.vue로 그대로 릴레이한다(Step3는 중계만 하고 상태를 보관하지 않음).
        relaySelfIssueDetected: (payload) => {
          this.$emit("self-issue-detected", payload);
        },
      };
    },

    getCardTabLabel(tooth, index) {
      if (tooth.toothNumbers && tooth.toothNumbers.length > 0) {
        const sorted = [...tooth.toothNumbers].sort((a, b) => a - b);
        return this.$t("workorder_tooth_tab", { no: sorted.join(", ") });
      }
      return this.$t("workorder_tooth_tab_index", { index: index + 1 });
    },

    getOtherConfirmedTeethNumbers(currentIndex) {
      const otherNumbers = [];
      this.confirmedTeeth.forEach((tooth, index) => {
        if (
          index !== currentIndex &&
          tooth.toothNumbers &&
          tooth.toothNumbers.length > 0
        ) {
          otherNumbers.push(...tooth.toothNumbers);
        }
      });
      return otherNumbers;
    },

    // SPEC-CHINA-CERT-CARD-LOOKUP-UNIFY-001 REQ-FE-STEP3-004 — 같은(미저장) 작업지시서 내
    // 다른 치아가 이미 선택한 randomNo 목록. currentIndex=-1 이면 입력카드(inputToothInfo) 기준
    // (즉 모든 confirmedTeeth의 randomNo), currentIndex>=0 이면 그 확정카드를 제외한
    // 나머지 confirmedTeeth + inputToothInfo의 randomNo.
    // Wave2 REQ-ADM-MULTI-004 — 카드가 다중 제품(products[])을 가질 수 있으므로 배열 전체를 펼쳐서 수집한다.
    // 레거시 스칼라 randomNo(비-덴탈맥스)도 병행 지원.
    getOtherAssignedRandomNos(currentIndex) {
      const collect = (tooth, bucket) => {
        if (!tooth) return;
        if (Array.isArray(tooth.products)) {
          tooth.products.forEach((product) => {
            if (product.randomNo) bucket.push(product.randomNo);
          });
        }
        if (tooth.randomNo) bucket.push(tooth.randomNo);
      };

      const others = [];
      this.confirmedTeeth.forEach((tooth, index) => {
        if (index !== currentIndex) collect(tooth, others);
      });
      // 확정카드를 보는 중이면 입력카드(작성중)의 randomNo(들)도 포함
      if (currentIndex >= 0) {
        collect(this.inputToothInfo, others);
      }
      return others;
    },

    // Wave2 DEC-009 — 카드의 제품 정체성을 products[] 단일 shape으로 정규화한다.
    // 덴탈맥스는 태그형 멀티선택 결과(products)를 그대로 쓰고, 그 외 제조사는 레거시 스칼라
    // (itemName/productInfoWithCode/randomNo)를 단일 엔트리 배열로 변환한다(REQ-ADM-MULTI-008 무변경).
    normalizeCardProducts(tooth) {
      if (Array.isArray(tooth.products) && tooth.products.length > 0) {
        return tooth.products.map((product) => ({ ...product }));
      }
      // 덴탈맥스는 itemName만으로 제품을 만들지 않는다 — randomNo 없는 name-only 엔트리가
      // payload(toothDetails[].products)에 직렬화되는 것을 막는 마지막 방어선(hasCardProduct와 동일 원칙).
      if (isDentalmaxGroup(tooth.group)) return [];
      const itemName = tooth.itemName || tooth.productInfoWithoutCode?.brand || "";
      if (!itemName) return [];
      return [
        {
          randomNo: tooth.randomNo || "",
          gtinCode: tooth.productInfoWithCode?.gtinCode || "",
          itemName,
          lotNumber: tooth.productInfoWithCode?.lotNumber || "",
          manufacturingDate: tooth.productInfoWithCode?.manufacturingDate || "",
          productCode:
            tooth.productInfoWithCode?.productCode ||
            tooth.productInfoWithCode?.gtinCode ||
            "",
        },
      ];
    },

    // Wave2 REQ-ADM-MULTI-007 — 카드에 제품(products)이 최소 1개 있는지 판정하는 공용 술어(SSOT).
    // 덴탈맥스(products 배열)와 비-덴탈맥스(레거시 스칼라) 양쪽을 모두 인정한다.
    // @MX:ANCHOR: [AUTO] confirmAndAddCard·validateForm(4곳)·확정카드 검증 루프가 모두 이 술어 하나로만
    //   "카드 있음"을 판정한다 — 호출부가 각자 products.length 등 임시 체크를 만들면 이 가드가 무력화된다.
    // @MX:REASON: [AUTO] 실물카드 없이 제출되는 회귀(운영 오염 1건, 2026-08-13)의 재발 방지 SSOT.
    hasCardProduct(tooth) {
      if (!tooth) return false;
      // 덴탈맥스는 itemName 폴백을 인정하지 않는다 — 실제로 선택된 실물카드(randomNo 보유)가
      // products[]에 최소 1개 있어야만 "카드 있음"으로 판정한다(자동 제조사 선택 시 pre-fill된
      // itemName만으로는 통과시키지 않음, 원인 ①②③ 공통 차단점).
      if (isDentalmaxGroup(tooth.group)) {
        return (
          Array.isArray(tooth.products) &&
          tooth.products.some((product) => !!product.randomNo)
        );
      }
      if (Array.isArray(tooth.products) && tooth.products.length > 0) return true;
      return !!(tooth.itemName || tooth.productInfoWithoutCode?.brand);
    },

    updateModalShowHistory(value) {
      this.modals.history = {
        ...this.modals.history,
        ...value,
      };
    },

    // @MX:NOTE: [AUTO] china-inapi GET /china/manufacturers 실데이터로 교체 — 레거시 한국 inapi 하드코딩 제거
    async loadManufacturerList() {
      try {
        this.$store.commit("setIsLoading", true);
        const response = await getManufacturers({ medicalDept: 'DENT', productType: 'ZIRCONIA' });

        // response.data 가 flat 배열
        const list = Array.isArray(response.data) ? response.data : [];
        if (list.length > 0) {
          this.companiesByApi.certifiedCompanies = list.filter((item) => item.isCertified);
          this.companiesByApi.notCertifiedCompanies = list.filter((item) => !item.isCertified);
        }
      } catch (error) {
        console.error("제조사 목록 로드 실패:", error);
      } finally {
        this.$store.commit("setIsLoading", false);
      }
    },

    updateManufacturerList(result) {
      this.companiesByApi.certifiedCompanies = result.filter((item) => item.isCertified);
      this.companiesByApi.notCertifiedCompanies = result.filter((item) => !item.isCertified);
    },

    async validateForm() {
      // 플래그 초기화
      this.includeInputInPublish = false;

      const hasConfirmedTeeth = this.confirmedTeeth.length > 0;
      const hasInputData =
        (this.inputToothInfo.toothNumbers &&
          this.inputToothInfo.toothNumbers.length > 0) ||
        this.inputToothInfo.treatmentType;
      const isInputComplete =
        this.inputToothInfo.toothNumbers &&
        this.inputToothInfo.toothNumbers.length > 0 &&
        this.inputToothInfo.treatmentType &&
        this.inputToothInfo.group &&
        this.hasCardProduct(this.inputToothInfo);

      // 케이스 1: 확정된 카드도 없고 입력 폼도 비어있음
      if (!hasConfirmedTeeth && !hasInputData) {
        await oneBtnDialog(
          this.$t("workorder_err_tooth_title"),
          this.$t("workorder_select_tooth"),
          false,
          this.$t("workorder_btn_confirm")
        );
        return false;
      }

      // 케이스 2: 확정된 카드가 있고, 입력 폼에도 작성중인 데이터가 있음
      if (hasConfirmedTeeth && hasInputData) {
        if (isInputComplete) {
          // 완전한 데이터가 있으면 확인 메시지
          const reverseButtons = false;
          const response = await twoBtnDialog(
            this.$t("workorder_modal_data_check_title"),
            this.$t("workorder_modal_data_check_msg"),
            this.$t("workorder_modal_btn_include"),
            this.$t("workorder_modal_btn_confirmed_only"),
            reverseButtons
          );

          if (response) {
            // "함께 발행" 선택 (confirmButton)
            this.includeInputInPublish = true;
          } else {
            // "확정된 것만 발행" 선택 (cancelButton)
            this.includeInputInPublish = false;
          }
        } else {
          const reverseButtons = false;
          // 불완전한 데이터가 있을 때도 선택권 제공
          const response = await twoBtnDialog(
            this.$t("workorder_modal_data_check_title"),
            this.$t("workorder_modal_data_partial_msg"),
            this.$t("workorder_modal_btn_include"),
            this.$t("workorder_modal_btn_confirmed_only"),
            reverseButtons
          );

          if (response) {
            // "함께 발행" 선택 (confirmButton) - 입력 폼 데이터 검증
            if (
              !this.inputToothInfo.toothNumbers ||
              this.inputToothInfo.toothNumbers.length === 0
            ) {
              await oneBtnDialog(
                this.$t("workorder_err_tooth_title"),
                this.$t("workorder_err_tooth_msg"),
                false,
                this.$t("workorder_btn_confirm")
              );
              return false;
            }
            if (!this.inputToothInfo.treatmentType) {
              await oneBtnDialog(
                this.$t("workorder_err_surgery_title"),
                this.$t("workorder_err_surgery_msg"),
                false,
                this.$t("workorder_btn_confirm")
              );
              return false;
            }
            if (!this.inputToothInfo.group) {
              await oneBtnDialog(
                this.$t("workorder_err_manufacturer_title"),
                this.$t("workorder_err_manufacturer_msg"),
                false,
                this.$t("workorder_btn_confirm")
              );
              return false;
            }
            if (!this.hasCardProduct(this.inputToothInfo)) {
              if (this.inputToothInfo.gtinSearchError) {
                await oneBtnDialog(
                  this.$t("workorder_err_barcode_title"),
                  this.$t("workorder_err_barcode_msg"),
                  false,
                  this.$t("workorder_btn_confirm")
                );
              } else {
                await oneBtnDialog(
                  this.$t("workorder_err_product_title"),
                  this.$t("workorder_err_product_msg"),
                  false,
                  this.$t("workorder_btn_confirm")
                );
              }
              return false;
            }
            this.includeInputInPublish = true;
          } else {
            // "확정된 것만 발행" 선택 (cancelButton)
            this.includeInputInPublish = false;
          }
        }
      }

      // 케이스 3: 확정된 카드가 없고 입력 폼에만 데이터가 있음
      if (!hasConfirmedTeeth && hasInputData) {
        // 입력 폼 데이터 검증
        if (
          !this.inputToothInfo.toothNumbers ||
          this.inputToothInfo.toothNumbers.length === 0
        ) {
          await oneBtnDialog(
            this.$t("workorder_err_tooth_title"),
            this.$t("workorder_err_tooth_msg"),
            false,
            this.$t("workorder_btn_confirm")
          );
          return false;
        }
        if (!this.inputToothInfo.treatmentType) {
          await oneBtnDialog(
            this.$t("workorder_err_surgery_title"),
            this.$t("workorder_err_surgery_msg"),
            false,
            this.$t("workorder_btn_confirm")
          );
          return false;
        }
        if (!this.inputToothInfo.group) {
          await oneBtnDialog(
            this.$t("workorder_err_manufacturer_title"),
            this.$t("workorder_err_manufacturer_msg"),
            false,
            this.$t("workorder_btn_confirm")
          );
          return false;
        }
        if (!this.hasCardProduct(this.inputToothInfo)) {
          if (this.inputToothInfo.gtinSearchError) {
            await oneBtnDialog(
              this.$t("workorder_err_barcode_title"),
              this.$t("workorder_err_barcode_msg"),
              false,
              this.$t("workorder_btn_confirm")
            );
          } else {
            await oneBtnDialog(
              this.$t("workorder_err_product_title"),
              this.$t("workorder_err_product_msg"),
              false,
              this.$t("workorder_btn_confirm")
            );
          }
          return false;
        }
        // 자동으로 발행하겠다고 간주
        this.includeInputInPublish = true;
      }

      // 확정된 카드들 검증
      for (const tooth of this.confirmedTeeth) {
        if (!tooth.group) {
          await oneBtnDialog(
            this.$t("workorder_err_manufacturer_title"),
            this.$t("workorder_err_manufacturer_msg"),
            false,
            this.$t("workorder_btn_confirm")
          );
          return false;
        }
        if (!this.hasCardProduct(tooth)) {
          if (tooth.gtinSearchError) {
            await oneBtnDialog(
              this.$t("workorder_err_barcode_title"),
              this.$t("workorder_err_barcode_msg"),
              false,
              this.$t("workorder_btn_confirm")
            );
          } else {
            await oneBtnDialog(
              this.$t("workorder_err_product_title"),
              this.$t("workorder_err_product_msg"),
              false,
              this.$t("workorder_btn_confirm")
            );
          }
          return false;
        }
      }

      return true;
    },

    getFormData() {
      console.log("[getFormData] confirmedTeeth:", this.confirmedTeeth);
      console.log("[getFormData] inputToothInfo:", this.inputToothInfo);

      const teethToProcess = [...this.confirmedTeeth];

      if (
        this.includeInputInPublish &&
        this.inputToothInfo.toothNumbers &&
        this.inputToothInfo.toothNumbers.length > 0
      ) {
        teethToProcess.push(this.inputToothInfo);
      }

      const allSelectedTeeth = [];
      teethToProcess.forEach((tooth) => {
        if (tooth.toothNumbers && tooth.toothNumbers.length > 0) {
          allSelectedTeeth.push(...tooth.toothNumbers);
        }
      });

      // 카드별로 묶어서 전송 + 작성중인 데이터도 저장
      const result = {
        prostheticType:
          teethToProcess[0]?.treatmentType?.toLowerCase() || "crown",
        selectedTeeth: allSelectedTeeth,
        // Wave2 DEC-009/REQ-ADM-MULTI-007 — 카드(제품) 정체성은 products[] 단일 shape으로 통일한다.
        // 덴탈맥스는 태그형 멀티선택 결과(N개), 그 외 제조사는 레거시 스칼라를 1개짜리 배열로 정규화
        // (normalizeCardProducts). 레거시 scalar 필드(product/gtinCode/lotNumber/manufacturingDate/
        // randomNo)는 더 이상 여기서 직접 계산하지 않고, BE POST 시점(WorkOrderService.prepareWorkOrderData)
        // 에서 products[]를 펼쳐 엔트리별로 채운다 — Step3 왕복 무손실을 위해 products[] 원본을 보존.
        toothDetails: teethToProcess.map((tooth) => ({
          toothNumbers: tooth.toothNumbers || [],
          treatmentType: tooth.treatmentType,
          manufacturer: tooth.group,
          manufacturerId: tooth.manufacturerId || "",
          detailSelection: tooth.detailSelection || "ZIRCONIA",
          stm: tooth.switches?.stm || false,
          products: this.normalizeCardProducts(tooth),
        })),
        // 작성중인 데이터도 함께 저장 (Step4에서 돌아올 때 복원용)
        inputToothInfo: { ...this.inputToothInfo },
      };

      console.log("[getFormData] result:", result);
      return result;
    },

    restoreInitialData() {
      console.log("[restoreInitialData] 시작");
      console.log("[restoreInitialData] initialData:", this.initialData);
      console.log(
        "[restoreInitialData] toothDetails:",
        this.initialData?.toothDetails
      );
      console.log(
        "[restoreInitialData] inputToothInfo:",
        this.initialData?.inputToothInfo
      );

      // 작성중인 데이터(inputToothInfo) 복원
      if (this.initialData?.inputToothInfo) {
        console.log(
          "[Step3] 작성중인 데이터 복원:",
          this.initialData.inputToothInfo
        );
        this.inputToothInfo = { ...this.initialData.inputToothInfo };
      }

      // 확정된 카드들(toothDetails) 복원 - 단, inputToothInfo에 포함된 데이터는 제외
      if (
        this.initialData &&
        this.initialData.toothDetails &&
        this.initialData.toothDetails.length > 0
      ) {
        console.log(
          "[Step3] 확정 데이터 복원 실행:",
          this.initialData.toothDetails
        );

        // inputToothInfo와 동일한 데이터는 제외하고 확정된 카드만 복원
        const inputToothNumbers =
          this.initialData.inputToothInfo?.toothNumbers || [];
        const confirmedDetails = this.initialData.toothDetails.filter(
          (detail) => {
            // 작성중인 데이터와 동일한 치아번호 조합이면 제외
            const detailNumbers = detail.toothNumbers || [];
            return !this.arraysEqual(detailNumbers, inputToothNumbers);
          }
        );

        // Wave2 P1-3/REQ-ADM-MULTI-007/AC-020 — products[] 배열을 순서 그대로 복원(무손실 왕복).
        // 레거시 스칼라(itemName/productInfoWithCode/randomNo)는 products[0]에서 파생해 비-덴탈맥스
        // 표시 및 하위호환을 유지한다(덴탈맥스 멀티카드는 이 스칼라들을 더 이상 읽지 않음).
        this.confirmedTeeth = confirmedDetails.map((detail) => {
          const products = Array.isArray(detail.products)
            ? detail.products.map((product) => ({ ...product }))
            : [];
          const firstProduct = products[0] || {};

          return {
            toothNumbers: detail.toothNumbers || [],
            treatmentType: detail.treatmentType,
            group: detail.manufacturer,
            detailSelection: detail.detailSelection || "ZIRCONIA",
            itemName: firstProduct.itemName || "",
            isCertified: true,
            willTypeProductInfoStepOne: false,
            manufacturerId: detail.manufacturerId || "",
            productInfoWithoutCode: {
              brand: firstProduct.itemName || "",
              customBrand: "",
              spec: "",
              customSpec: "",
            },
            productInfoWithCode: {
              id: firstProduct.randomNo || "",
              productCode: firstProduct.productCode || firstProduct.gtinCode || "",
              itemName: firstProduct.itemName || "",
              description: "",
              gtinCode: firstProduct.gtinCode || "",
              manufacturingDate: firstProduct.manufacturingDate || "",
              lotNumber: firstProduct.lotNumber || "",
            },
            switches: {
              stm: detail.stm || false,
            },
            // SPEC-CHINA-CERT-CARD-LOOKUP-UNIFY-001 REQ-FE-STEP3-003 — 레거시 스칼라 randomNo(하위호환)
            randomNo: firstProduct.randomNo || "",
            // Wave2 DEC-009 — 카드(제품) 배열 무손실 복원(순서 보존, 덴탈맥스 태그형 멀티선택 상태)
            products,
          };
        });

        console.log("[Step3] 복원된 confirmedTeeth:", this.confirmedTeeth);

        if (this.confirmedTeeth.length > 0) {
          this.currentConfirmedIndex = 0;
        }
      } else {
        console.log("[Step3] 복원할 확정 데이터가 없음");
      }
    },

    // 배열이 동일한지 확인하는 헬퍼 함수
    arraysEqual(a, b) {
      if (a.length !== b.length) return false;
      const sortedA = [...a].sort((x, y) => x - y);
      const sortedB = [...b].sort((x, y) => x - y);
      return sortedA.every((val, index) => val === sortedB[index]);
    },
  },
};
</script>

<style scoped lang="scss">
.prosthetic-v2-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
}

.input-card-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.add-button-container {
  display: flex;
  justify-content: center;
  padding: 20px 0;
  background-color: white;
  margin-top: -16px;
  padding-top: 0;
}

.add-card-button {
  min-width: 600px;
  height: 48px;
  color: #17191c;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: #1a6fcc;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(39, 138, 236, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
}

.confirmed-cards-section {
  margin-top: 24px;
}

// 탭 컨테이너
.card-tabs-container {
  display: flex;
  overflow-x: auto;
  gap: 10px;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 3px;
  }
}

// 개별 탭 버튼
.card-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background-color: #eff4ff;
  border: 2px solid transparent;
  border-radius: 0px;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  z-index: 1;

  &:hover {
    background-color: #e5e7eb;
    color: #374151;
  }

  &.active {
    background-color: #fff;
    font-weight: 600;

    .tab-label {
      color: #000;
    }
  }
}

.tab-label {
  flex: 1;
  color: inherit;
}

.tab-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;

  i {
    font-size: 12px;
    color: #9ca3af;
  }

  &:hover {
    background-color: rgba(239, 68, 68, 0.1);

    i {
      color: #ef4444;
    }
  }
}
</style>
